/**
 * ============================================================================
 * NOTE: Some types have moved to the @jslib/shared package.
 * ============================================================================
 */
import { VerificationStepsEnum } from "@jslib/shared/runtimeTypes";
import { logger } from "@jslib/shared/utils/logger";

import { Locales } from "../intl/locales";

import {
  type FirstResponderStatus,
  type LowIncomeStatus,
  type MedicalProfessionalStatus,
  type MilitaryStatus,
  type RecentMoverStatus,
  type Segment,
  type SuccessResponse,
  type VerificationResponse,
  type VerificationStep,
} from "./types";

export * from "@jslib/shared/runtimeTypes";
export { Locales };

/**
 * @description List of locales supported by SheerID
 */
export type Locale = (typeof Locales)[number];

export const registerAdditionalLocales = (locales: Locale[]): void => {
  logger.info(`Registering additional locales ${locales.join(", ")}`);
  (Locales as string[]).push(...locales);
};

export function isSuccessResponse(response: VerificationResponse): response is SuccessResponse {
  return response.currentStep === "success";
}

export enum TryAgainStepsEnum {
  docUpload = "docUpload",
  pending = "pending",
  error = "error",
}
export const TryAgainSteps = Object.values(TryAgainStepsEnum) as any; // For runtime checks

export enum MockStepsEnum {
  loading = "loading",
  collect = "collect",
}

/**
 * There are a few "steps" that aren't actually steps but are used to point at
 * the correct submission endpoint. This is a failure of modeling but until we
 * unwind that we don't want these steps to be "mockable"
 */
const unMockableSteps: VerificationStep[] = [
  "cancelDocUpload",
  "cancelEmailLoop",
  "cancelSocialSecurityNumber",
  "collectMilitaryStatus", // currently not a useful step to mock
];

export const MockSteps = [
  ...(Object.values(VerificationStepsEnum).filter(
    (step) => !unMockableSteps.includes(step),
  ) as any),
  ...(Object.values(MockStepsEnum) as any),
];

export enum RewardDisplayEnum {
  TEXT = "TEXT",
  QR = "QR",
  PDF417 = "PDF417",
  UPC_A = "UPC_A",
  ITF = "ITF",
  CODE_128 = "CODE_128",
}

export enum SegmentToStepMap {
  student = "collectStudentPersonalInfo",
  military = "collectMilitaryStatus",
  teacher = "collectTeacherPersonalInfo",
  firstResponder = "collectFirstResponderPersonalInfo",
  senior = "collectSeniorPersonalInfo",
  age = "collectAgePersonalInfo",
  medical = "collectMedicalProfessionalPersonalInfo",
  recentMover = "collectMoverPersonalInfo",
  licensedProfessional = "collectLicensedProfessionalPersonalInfo",
  identity = "collectGeneralIdentityPersonalInfo",
  member = "collectMemberPersonalInfo",
}

export enum SegmentEnum {
  STUDENT = "student",
  MILITARY = "military",
  TEACHER = "teacher",
  MEMBER = "member",
  SENIOR = "senior",
  AGE = "age",
  FIRST_RESPONDER = "firstResponder",
  MEDICAL = "medical",
  EMPLOYMENT = "employment",
  IDENTITY = "identity",
  LICENSED_PROFESSIONAL = "licensedProfessional",
  RECENT_MOVER = "recentMover",
  LOW_INCOME = "lowIncome",
}
export const Segments = Object.values(SegmentEnum) as Array<Segment>; // For runtime checks

export enum SubSegmentEnum {
  ACTIVE_DUTY = "activeDuty",
  VETERAN = "veteran",
  RESERVIST = "reservist",
  MILITARY_RETIREE = "retiree",
  MILITARY_FAMILY = "militaryFamily",
  GOLD_STAR_FAMILY = "goldStarFamily",
  FIREFIGHTER = "fireFighter",
  POLICE = "police",
  EMT = "emt",
  NURSE = "nurse",
  DRIVER_LICENSE = "driverLicense",
  GENERAL_IDENTITY = "generalIdentity",
  HYBRID_IDENTITY = "hybridIdentity",
  HOME_BUYER = "homeBuyer",
  OTHER_MOVER = "otherMover",
  LICENSED_COSMETOLOGIST = "licensedCosmetologist",
  LICENSED_REAL_ESTATE_AGENT = "licensedRealEstateAgent",
  VETERINARIAN = "veterinarian",
  SNAP_BENEFITS = "snapBenefits",
  OTHER_GOVERNMENT_ASSISTANCE = "otherGovernmentAssistance",
  CHILD_CARE_WORKER = "childCareWorker",
  LIBRARIAN = "librarian",
  INTERIOR_DESIGNER = "interiorDesigner",
  ARCHITECT = "architect",
  GENERAL_CONTRACTOR = "generalContractor",
  NUTRITION_PROFESSIONAL = "nutritionProfessional",
}
export const SubSegments = Object.values(SubSegmentEnum) as any; // For runtime checks

export const MilitaryStatusDefaultMessagesEnum: {
  [k in MilitaryStatus]: string;
} = {
  ACTIVE_DUTY: "Active Duty",
  MILITARY_RETIREE: "Military Retiree",
  RESERVIST: "Reservist or National Guard",
  VETERAN: "Military Veteran or Retiree",
  MILITARY_FAMILY: "Military Family",
  GOLD_STAR_FAMILY: "Gold Star Family",
} as const;

/**
 * @deprecated Do org search with `?tag={status}`, instead.
 * Kept here for es6 export, for now
 * e.g. ?tag=RESERVIST&country=US
 */
export const militaryReservistOrgNames = [
  // U.S.:
  "Air Force Reserve",
  "Air National Guard",
  "Army Reserve",
  "Army National Guard",
  "Coast Guard Reserve",
  "Marine Corps Forces Reserve",
  "Navy Reserve",
  // U.K.:
  "British Army Reserve",
  "Royal Air Force Reserve",
  "His Majesty's Naval Service Reserve",
  // Canada:
  "Naval Reserve",
  "Canadian Army Reserve",
  "Royal Canadian Air Force Reserve",
];

export const FirstResponderStatusDefaultMessagesEnum: {
  [k in FirstResponderStatus]: string;
} = {
  FIREFIGHTER: "Firefighter",
  POLICE: "Police",
  EMT: "EMT",
  SEARCH_AND_RESCUE: "Search and Rescue",
} as const;

export const MedicalProfessionalStatusDefaultMessagesEnum: {
  [k in MedicalProfessionalStatus]: string;
} = {
  NURSE: "Nurse",
  DOCTOR: "Doctor",
  OTHER_HEALTH_WORKER: "Other Health Worker",
  DENTIST: "Dentist",
  PHARMACIST: "Pharmacist",
} as const;

export const RecentMoverStatusDefaultMessagesEnum: {
  [k in RecentMoverStatus]: string;
} = {
  HOME_BUYER: "Home Buyer",
  OTHER_MOVER: "Other Mover",
} as const;

export const LowIncomeStatusDefaultMessagesEnum: {
  [k in LowIncomeStatus]: string;
} = {
  SNAP_BENEFITS: "Snap Benefits Recipient",
  OTHER_GOVERNMENT_ASSISTANCE: "Other Government Assistance Recipient",
  COMMUNITY_ELIGIBILITY_PROVISION: "CEP (Community Eligibility Provision, Title 1/LAP)",
} as const;

export const MilitaryStatusDefaultMessages = Object.values(
  MilitaryStatusDefaultMessagesEnum,
) as any; // For runtime checks
export const FirstResponderStatusDefaultMessages = Object.values(
  FirstResponderStatusDefaultMessagesEnum,
) as any; // For runtime checks
export const MedicalStatusdefaultMessages = Object.values(
  MedicalProfessionalStatusDefaultMessagesEnum,
) as any; // For runtime checks
export const RecentMoverStatusDefaultMessages = Object.values(
  RecentMoverStatusDefaultMessagesEnum,
) as any; // For runtime checks
export const LowIncomeStatusDefaultMessages = Object.values(
  LowIncomeStatusDefaultMessagesEnum,
) as any; // For runtime checks

export enum FieldIdEnum {
  firstName = "firstName",
  lastName = "lastName",
  memberId = "memberId",
  organization = "organization",
  birthDate = "birthDate",
  email = "email",
  phoneNumber = "phoneNumber",
  postalCode = "postalCode",
  address1 = "address1",
  city = "city",
  country = "country",
  state = "state",
  dischargeDate = "dischargeDate",
  activeDutyStartDate = "activeDutyStartDate",
  docUpload = "docUpload",
  status = "status",
  statuses = "statuses",
  marketConsentValue = "marketConsentValue",
  socialSecurityNumber = "socialSecurityNumber",
  carrierConsentValue = "carrierConsentValue",
  driverLicenseNumber = "driverLicenseNumber",
  ebtCardNumber = "ebtCardNumber",
  overrideCode = "overrideCode",
  organizationEmail = "organizationEmail",
  authenticationCode = "authenticationCode",
  cvecNumber = "cvecNumber",
  captchaToken = "captchaToken",
}
export const FieldIds = Object.values(FieldIdEnum) as any; // For runtime checks

export enum HookNameEnum {
  ON_VERIFICATION_READY = "ON_VERIFICATION_READY",
  ON_VERIFICATION_SUCCESS = "ON_VERIFICATION_SUCCESS",
  ON_VERIFICATION_STEP_CHANGE = "ON_VERIFICATION_STEP_CHANGE",
  ON_FORM_LOCALE_CHANGE = "ON_FORM_LOCALE_CHANGE",
}
export const HookNames = Object.values(HookNameEnum) as any;

export enum StateEnum {
  AK = "AK",
  AL = "AL",
  AR = "AR",
  AZ = "AZ",
  AS = "AS",
  CA = "CA",
  CO = "CO",
  CT = "CT",
  DC = "DC",
  DE = "DE",
  FL = "FL",
  GA = "GA",
  GU = "GU",
  HI = "HI",
  IA = "IA",
  ID = "ID",
  IL = "IL",
  IN = "IN",
  KS = "KS",
  KY = "KY",
  LA = "LA",
  MA = "MA",
  MD = "MD",
  ME = "ME",
  MI = "MI",
  MN = "MN",
  MO = "MO",
  MS = "MS",
  MT = "MT",
  NC = "NC",
  ND = "ND",
  NE = "NE",
  NH = "NH",
  NJ = "NJ",
  NM = "NM",
  NV = "NV",
  NY = "NY",
  OH = "OH",
  OK = "OK",
  OR = "OR",
  PA = "PA",
  PR = "PR",
  RI = "RI",
  SC = "SC",
  SD = "SD",
  TN = "TN",
  TX = "TX",
  UT = "UT",
  VA = "VA",
  VI = "VI",
  VT = "VT",
  WA = "WA",
  WI = "WI",
  WV = "WV",
  WY = "WY",
}

export enum OnfidoReportNames {
  DOCUMENT = "document",
  FACIAL_SIMILARITY_MOTION = "facial_similarity_motion",
}

export enum OnfidoDocumentTypes {
  PASSPORT = "passport",
  NATIONAL_IDENTITY_CARD = "national_identity_card",
  DRIVING_LICENCE = "driving_licence",
  RESIDENCE_PERMIT = "residence_permit",
}

export enum IdCheckLoopServiceType {
  ID_CHECK = "ID_CHECK",
  ID_CHECK_LIVENESS = "ID_CHECK_LIVENESS",
}

export enum ThirdPartyLoopProvider {
  SIGNZY = "signzy",
}
